import ISav, { getCurrentStatusLabelForGestionnaireFromStatus } from '../models/sav';
import { IMessage } from '../models/message';

export type PromptData = {
  sav: {
    id: number;
    status: string;
    statusLabel: string;
    dateDeclaration: string;
    dateSinistre: string;
    dateCreation: string;
    dateModificationDeclarant: string;
    dateModificationOperateur: string;
    tags: string[];
    description?: string;
    montantSeller?: number;
    montantCommercial?: number;
    montantBeneficiary?: number;
    plafondFinancierAlerte?: string;
    plafondKmAlerte?: string;
    joursDepuisDeclaration?: number;
    joursRestantsGarantie?: number;
    pourcentageGarantieEcoulee?: number;
    plafondFinancierRestant?: number;
    plafondKmRestant?: number;
    plafondFinancierUtilise?: number;
    plafondKmUtilise?: number;
    plafondFinancierDepasse?: boolean;
    plafondKmDepasse?: boolean;
    preAnalyseSynthese?: string;
    formValues?: Array<{ label: string; value: string }>;
    currentKm?: number;
    historiqueStatuts?: Array<{ date: string; status: string; label: string }>;
  };
  contract: {
    label: string;
    dateSouscription: string;
    dateEnd: string;
    companyName: string;
    vendorType: string;
    plafondFinancier?: number;
    limiteKm?: number;
    consignes?: string;
    savManager?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
    };
  };
  warranty?: {
    name: string;
    duration: number;
    options: string[];
    financialCap?: number;
    kmLimit?: number;
  };
  beneficiary: {
    firstName: string;
    lastName: string;
    address: string;
    cp: string;
    city: string;
    email: string;
    phoneNumber: string;
    phoneNumber2?: string;
  };
  vehicle?: {
    registration?: string;
    brand?: string;
    model?: string;
    fiscalPower?: string;
    entryIntoService?: string;
    kmStart?: string;
  };
  reparateur?: {
    nom?: string;
    adresse?: string;
    cp?: string;
    ville?: string;
    email?: string;
    telephone?: string;
    isReparator?: boolean;
  } | undefined;
  devis?: {
    id?: number;
    label?: string;
    dateCreation?: string;
    downloadUrl?: string;
  } | undefined;
  bonPourAccord?: {
    decision?: string;
    date?: string;
    comment?: string;
    montantVendeur?: number;
    montantCommercial?: number;
    details?: Array<{ label: string; montant: number; quantite: number; montantTotal: number }>;
    sentToSeller?: boolean;
    sentToSellerDateAt?: string;
    sentToCustomer?: boolean;
    sentToCustomerDateAt?: string;
    sentToRepairer?: boolean;
    sentToRepairerDateAt?: string;
    signedByCustomer?: boolean;
    signedByCustomerDateAt?: string;
    signedBySeller?: boolean;
    signedBySellerDateAt?: string;
  } | undefined;
  historiqueBeneficiaire?: Array<{
    date: string;
    auteur: string;
    texte: string;
  }>;
};

export function extractPromptDataFromSav(sav: ISav, beneficiaryMessages?: IMessage[]): PromptData {
  // Extraction des helpers et calculs avancés
  const contract = sav.contract;
  const warranty = contract?.warranty;
  const beneficiary = contract?.beneficiary;

  const statusLabel = getCurrentStatusLabelForGestionnaireFromStatus(sav.status);
  // Champs dynamiques véhicule
  const valForm = (key: string) => contract?.valFormulaires?.find(f => f.champFormulaire.key === key)?.valText || undefined;
  // Champs dynamiques SAV
  const getFormValue = (key: string) => sav.savFormValues?.find(f => f.formField.key === key);
  // Champs dynamiques (tous)
  const formValues = sav.savFormValues?.map(f => {
    let value = '';
    if (f.valueDate) {
      try {
        value = new Date(f.valueDate).toLocaleDateString('fr-FR');
      } catch {
        value = f.valueDate;
      }
    } else if (f.value) {
      value = f.value;
    } else if (typeof f.valueInt === 'number' && f.valueInt !== null) {
      value = String(f.valueInt);
    } else if (f.valueText) {
      value = f.valueText;
    } else if (f.valueTextarea) {
      value = f.valueTextarea;
    } else {
      value = 'Non renseigné';
    }
    return { label: f.formField.labelConsult, value };
  });
  // Calculs avancés (extraits de SavSummaryCard, SavAnalysisCard, etc.)
  const dateDeclaration = new Date(sav.dateDeclaration);
  const dateEnd = new Date(contract?.dateEnd);
  const dateSouscription = new Date(contract?.dateSouscription);
  const daysSinceDeclaration = Math.floor((Date.now() - dateDeclaration.getTime()) / (1000 * 60 * 60 * 24));
  const daysRemainingWarranty = Math.floor((dateEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  const totalWarrantyDays = Math.floor((dateEnd.getTime() - dateSouscription.getTime()) / (1000 * 60 * 60 * 24));
  const daysUsed = Math.floor((Date.now() - dateSouscription.getTime()) / (1000 * 60 * 60 * 24));
  const percentageUsed = totalWarrantyDays > 0 ? Math.min(100, Math.max(0, Math.round((daysUsed / totalWarrantyDays) * 100))) : undefined;
  // Plafonds
  const kmLimit = contract?.warrantyKmLimit;
  const financialCap = contract?.warrantyFinancialCap;
  // Champs dynamiques véhicule
  const kmStart = valForm('km_start') ? parseInt(valForm('km_start') as string, 10) : undefined;
  const currentKm = getFormValue('current_km')?.valueText ? parseInt(getFormValue('current_km')?.valueText as string, 10) : undefined;
  let plafondKmRestant, plafondKmDepasse, plafondKmAlerte;
  if (kmLimit && kmStart !== undefined && currentKm !== undefined) {
    const absoluteKmLimit = kmLimit + kmStart;
    plafondKmRestant = absoluteKmLimit - currentKm;
    plafondKmDepasse = plafondKmRestant < 0;
    plafondKmAlerte = plafondKmDepasse ? 'Plafond kilométrique dépassé' : undefined;
  }
  // Plafond financier (mock: à remplacer par la somme réelle des SAV liés)
  let plafondFinancierRestant, plafondFinancierDepasse, plafondFinancierAlerte, plafondFinancierUtilise;
  if (financialCap) {
    // TODO: remplacer 0 par la somme réelle des montants déjà pris en charge sur ce contrat
    plafondFinancierRestant = financialCap - 0;
    plafondFinancierDepasse = plafondFinancierRestant < 0;
    plafondFinancierAlerte = plafondFinancierDepasse ? 'Plafond financier dépassé' : undefined;
    plafondFinancierUtilise = 0;
  }
  // Synthèse pré-analyse
  let preAnalyseSynthese = '';
  if (plafondKmAlerte) preAnalyseSynthese += plafondKmAlerte + '. ';
  if (plafondFinancierAlerte) preAnalyseSynthese += plafondFinancierAlerte + '. ';
  if (!plafondKmAlerte && !plafondFinancierAlerte) preAnalyseSynthese = 'Aucune alerte de plafond.';
  // Historique statuts (mock: à enrichir si historique réel)
  const historiqueStatuts = [];
  // Consignes et gestionnaire SAV
  const consignes = contract?.company?.savGuidelines;
  const savManager = contract?.company ? {
    firstName: contract.company.savManagerFirstname,
    lastName: contract.company.savManagerLastname,
    email: contract.company.savManagerEmail,
    phone: contract.company.savManagerPhone,
  } : undefined;
  // Réparateur
  let reparateur: PromptData['reparateur'] = undefined;
  if (sav.acceptedQuote && sav.acceptedQuote.company) {
    const c = sav.acceptedQuote.company;
    reparateur = {
      nom: c.name,
      adresse: c.address,
      cp: c.cp,
      ville: c.city,
      email: c.email,
      telephone: c.phone,
      isReparator: c.isReparator,
    };
  }
  // Devis
  let devis: PromptData['devis'] = undefined;
  if (sav.acceptedQuote && sav.acceptedQuote.document) {
    const d = sav.acceptedQuote.document;
    devis = {
      id: d.id,
      label: d.label,
      dateCreation: d.dateCreation,
      downloadUrl: d.downloadUrl,
    };
  }
  // Bon pour accord/refus
  let bonPourAccord: PromptData['bonPourAccord'] = undefined;
  if (sav.decisionVoucher) {
    const v = sav.decisionVoucher;
    bonPourAccord = {
      decision: v.decision,
      date: v.date,
      comment: v.comment,
      montantVendeur: v.amountSeller,
      montantCommercial: v.amountSellerCommercial,
      details: v.amountDetails?.map(d => ({ label: d.label, montant: d.amountSeller, quantite: d.quantity, montantTotal: d.amountTotalTTC })),
      sentToSeller: v.sentToSeller,
      sentToSellerDateAt: v.sentToSellerDateAt,
      sentToCustomer: v.sentToCustomer,
      sentToCustomerDateAt: v.sentToCustomerDateAt,
      sentToRepairer: v.sentToRepairer,
      sentToRepairerDateAt: v.sentToRepairerDateAt,
      signedByCustomer: v.signedByCustomer,
      signedByCustomerDateAt: v.signedByCustomerDateAt,
      signedBySeller: v.signedBySeller,
      signedBySellerDateAt: v.signedBySellerDateAt,
    };
  }
  // Historique messages bénéficiaire
  let historiqueBeneficiaire: PromptData['historiqueBeneficiaire'] = undefined;
  if (beneficiaryMessages && beneficiaryMessages.length > 0) {
    historiqueBeneficiaire = beneficiaryMessages.map(msg => ({
      date: msg.dateCreation,
      auteur: msg.user ? `${msg.user.firstName} ${msg.user.lastName}` : '',
      texte: msg.content.text,
    }));
  }
  return {
    sav: {
      id: sav.id,
      status: String(sav.status),
      statusLabel,
      dateDeclaration: sav.dateDeclaration,
      dateSinistre: sav.dateSinistre,
      dateCreation: sav.dateCreation,
      dateModificationDeclarant: sav.dateModificationDeclarant,
      dateModificationOperateur: sav.dateModificationOperateur,
      tags: sav.savTagsRelationship.map(t => t.tag.value),
      description: sav.savFormValues?.find(f => f.formField.key === 'description')?.valueText,
      montantSeller: sav.amountSeller,
      montantCommercial: sav.amountSellerCommercial,
      montantBeneficiary: sav.amountBeneficiary,
      plafondFinancierAlerte,
      plafondKmAlerte,
      joursDepuisDeclaration: daysSinceDeclaration,
      joursRestantsGarantie: daysRemainingWarranty,
      pourcentageGarantieEcoulee: percentageUsed,
      plafondFinancierRestant,
      plafondKmRestant,
      plafondFinancierUtilise,
      plafondKmUtilise: undefined, // TODO: calculer si besoin
      plafondFinancierDepasse,
      plafondKmDepasse,
      preAnalyseSynthese,
      formValues,
      currentKm,
      historiqueStatuts,
    },
    contract: {
      label: contract?.label,
      dateSouscription: contract?.dateSouscription,
      dateEnd: contract?.dateEnd,
      companyName: contract?.company?.name,
      vendorType: contract?.company?.vendorType === 0 ? 'Vendeur' : 'Intermédiaire',
      plafondFinancier: contract?.warrantyFinancialCap,
      limiteKm: contract?.warrantyKmLimit,
      consignes,
      savManager,
    },
    warranty: warranty ? {
      name: warranty.name,
      duration: warranty.duration,
      options: warranty.options,
      financialCap: warranty.financialCap,
      kmLimit: warranty.kmLimit,
    } : undefined,
    beneficiary: {
      firstName: beneficiary?.firstName,
      lastName: beneficiary?.lastName,
      address: beneficiary?.address,
      cp: beneficiary?.cp,
      city: beneficiary?.city,
      email: beneficiary?.email,
      phoneNumber: beneficiary?.phoneNumber,
      phoneNumber2: beneficiary?.phoneNumber2,
    },
    vehicle: {
      registration: valForm('registration'),
      brand: valForm('brand'),
      model: valForm('model'),
      fiscalPower: valForm('fiscal_power'),
      entryIntoService: valForm('entry_into_service'),
      kmStart: valForm('km_start'),
    },
    reparateur,
    devis,
    bonPourAccord,
    historiqueBeneficiaire,
  };
}